import React, { useState, useEffect, useCallback } from "react";
import {
  IoSchoolOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoReceiptOutline,
  IoPrintOutline,
  IoCloseOutline,
} from "react-icons/io5";
import ProgressBar from "../components/ProgressBar";
import HistoryCard from "../components/HistoryCard";
import HistorySkeleton from "../components/skeleton-loading/HistorySkeleton";
import ReceiptModal from "../components/ReceiptModal";

import { useData } from "../data/DataProvider";

export default function History() {
  const {
    paymentHistoryData,
    installmentsSummaryData,
    activeRouteData,
    studentData,
    getAllData,
    loading,
  } = useData();

  const [currentRouteIndex, setCurrentRouteIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);

  // Receipt Modal State
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  const fetchInitialData = useCallback(async () => {
    try {
      await getAllData();
    } catch (error) {
      console.error("Veri çekilirken hata oluştu:", error);
    }
  }, [getAllData]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const historyRoutes = Array.isArray(paymentHistoryData)
    ? paymentHistoryData
    : [];
  const summaryRoutes = Array.isArray(installmentsSummaryData)
    ? installmentsSummaryData
    : [];

  const activeHistoryRoute = historyRoutes[currentRouteIndex] || historyRoutes[0] || null;
  const activeSummaryRoute =
    summaryRoutes.find(
      (s) => s.student_route_id === activeHistoryRoute?.student_route_id,
    ) ||
    summaryRoutes[currentRouteIndex] ||
    summaryRoutes[0] ||
    null;

  const handlePrevRoute = useCallback(() => {
    if (currentRouteIndex > 0) {
      setCurrentRouteIndex((prev) => prev - 1);
    }
  }, [currentRouteIndex]);

  const handleNextRoute = useCallback(() => {
    if (currentRouteIndex < historyRoutes.length - 1) {
      setCurrentRouteIndex((prev) => prev + 1);
    }
  }, [currentRouteIndex, historyRoutes.length]);

  const schoolName = activeHistoryRoute?.route_name || activeSummaryRoute?.route_name || "Güzergah Bilgisi Yok";

  const studentFullName =
    activeHistoryRoute?.student_name ||
    activeSummaryRoute?.student_name ||
    activeRouteData?.student_name ||
    (studentData?.name && studentData?.surname
      ? `${studentData.name} ${studentData.surname}`
      : studentData?.name || "");

  const allInstallments = activeSummaryRoute?.installments || [];
  const completedPayments = activeHistoryRoute?.completed_payments || [];

  const totalPaidCalc = completedPayments.reduce(
    (sum, item) => sum + Number(item.paid_amount || item.amount || 0),
    0,
  );

  const transactionCount = completedPayments.length;

  const totalInstallmentsCount = allInstallments.length;
  const paidInstallmentsCount = completedPayments.length;
  const progressPercentage =
    totalInstallmentsCount > 0
      ? Math.round((paidInstallmentsCount / totalInstallmentsCount) * 100)
      : 0;

  const paymentMethodMap = {
    cash: "Nakit Ödeme",
    credit_card: "Online Ödeme",
    bank_transfer: "Banka Transferi",
    online: "Online Ödeme",
    card: "Online Ödeme",
    transfer: "Banka Transferi",
  };

  const formattedTransactions = [...completedPayments]
    .sort((a, b) => new Date(b.paid_at || 0) - new Date(a.paid_at || 0))
    .map((pay, index) => {
      const formattedDate = pay.paid_at
        ? new Date(pay.paid_at).toLocaleDateString("tr-TR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
        : "-";

      const methodText =
        paymentMethodMap[pay.transaction_type] ||
        paymentMethodMap[pay.payment_method] ||
        pay.payment_method ||
        "Online Ödeme";

      const paidAmt = Number(pay.paid_amount || pay.amount || 0);
      const totalAmt = Number(pay.amount || 0);
      const remainingBal = Number(pay.balance ?? (totalAmt - paidAmt));

      const isPartial =
        pay.status === "partial" ||
        (remainingBal > 0 && pay.status !== "paid") ||
        (totalAmt > 0 && paidAmt < totalAmt && pay.status !== "paid");

      const displayAmount = `₺${paidAmt.toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
      })}`;

      return {
        keyId: pay.id || `${pay.installment_number}-${index}`,
        id: pay.installment_number,
        tarih: formattedDate,
        yontem: methodText,
        tutar: displayAmount,
        durum: isPartial ? "Kısmi Ödeme" : "Ödendi",
        rawItem: pay,
      };
    });

  const displayedTransactions = showAll
    ? formattedTransactions
    : formattedTransactions.slice(0, 3);

  const handleOpenReceiptModal = useCallback(
    (item) => {
      const paidAmt = Number(item.paid_amount || item.amount || 0);
      const dateFormatted = item.paid_at
        ? new Date(item.paid_at).toLocaleDateString("tr-TR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
        : "-";

      const methodText =
        paymentMethodMap[item.transaction_type] ||
        paymentMethodMap[item.payment_method] ||
        "Online Ödeme";

      const receiptNo =
        item.receipt_number ||
        item.receipt_no ||
        `T${String(item.id || item.installment_number || 1).padStart(6, "0")}`;

      setSelectedReceipt({
        studentName: studentFullName || "-",
        installmentNumber: item.installment_number || 1,
        date: dateFormatted,
        receiptNo: receiptNo.startsWith("#") ? receiptNo : `#${receiptNo}`,
        paymentMethod: methodText,
        amount: paidAmt,
        amountFormatted: `₺${paidAmt.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
        receivedBy: item.collector_personnel || item.received_by || "Sistem",
        status: item.status === "partial" ? "Kısmi Ödeme" : "Başarılı",
      });

      setIsReceiptModalOpen(true);
    },
    [studentFullName],
  );

  const handleCloseReceiptModal = useCallback(() => {
    setIsReceiptModalOpen(false);
  }, []);

  const handleToggleShowAll = useCallback(() => {
    setShowAll((prev) => !prev);
  }, []);

  if (loading && !paymentHistoryData) {
    return <HistorySkeleton />;
  }

  return (
    <>
      <div className="min-h-screen bg-[#f8f9ff] pt-6 pb-12 antialiased">
        <div className="max-w-3xl mx-auto px-4 mt-2 md:mt-8 space-y-6">
          {historyRoutes.length > 1 && (
            <div className="flex items-center justify-between p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
              <button
                onClick={handlePrevRoute}
                disabled={currentRouteIndex === 0}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 cursor-pointer"
              >
                <IoChevronBackOutline size={24} />
              </button>

              <div className="flex-1 text-center px-4">
                <h2 className="text-base md:text-lg font-bold tracking-wide text-[#00306b] truncate">
                  {schoolName}
                </h2>
              </div>

              <button
                onClick={handleNextRoute}
                disabled={currentRouteIndex === historyRoutes.length - 1}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 cursor-pointer"
              >
                <IoChevronForwardOutline size={24} />
              </button>
            </div>
          )}

          <div className="bg-[#00306b] text-white rounded-2xl p-5 shadow-md border border-white/5">
            {studentFullName ? (
              <div className="flex items-center pb-3.5 mb-4 border-b border-white/20">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mr-3 flex-shrink-0">
                  <IoSchoolOutline size={20} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-blue-100 text-xs font-semibold uppercase tracking-wider">
                    Öğrenci
                  </p>
                  <p className="text-white text-xl font-bold mt-0.5 truncate">
                    {studentFullName}
                  </p>
                </div>
              </div>
            ) : null}

            <div className="flex justify-between items-start">
              <div>
                <p className="text-white/80 text-sm font-medium">Toplam Ödenen</p>
                <h3 className="text-2xl font-bold mt-1">
                  ₺
                  {totalPaidCalc.toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </h3>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-sm font-medium">İşlem Sayısı</p>
                <p className="text-2xl text-center font-bold mt-1">
                  {transactionCount}
                </p>
              </div>
            </div>

            <ProgressBar
              percentage={progressPercentage}
              showPercentage={false}
              title={`Yıllık planın %${progressPercentage}’si tamamlandı.`}
              titleColor="text-white"
              barColor="#FFFFFF"
              trackColor="#FFFFFF33"
            />
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center px-1">
              <h3 className="text-base md:text-lg font-bold text-gray-800 tracking-tight">
                İşlem Geçmişi
              </h3>
              {formattedTransactions.length > 3 && (
                <button
                  onClick={handleToggleShowAll}
                  className="text-sm font-semibold text-[#0052cc] hover:underline focus:outline-none cursor-pointer"
                >
                  {showAll ? "Daha Az Göster" : "Tümünü Gör"}
                </button>
              )}
            </div>

            {displayedTransactions.length > 0 ? (
              displayedTransactions.map((tx) => (
                <HistoryCard
                  key={tx.keyId}
                  id={tx.id}
                  tarih={tx.tarih}
                  yontem={tx.yontem}
                  tutar={tx.tutar}
                  durum={tx.durum}
                  onDownload={() => handleOpenReceiptModal(tx.rawItem)}
                />
              ))
            ) : (
              <div className="py-8 bg-white rounded-xl border border-dashed border-gray-200 text-center">
                <p className="text-sm text-gray-400 italic">
                  Henüz tamamlanmış bir ödeme işlemi bulunmuyor.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Makbuz Görünüm Modalı */}
      <ReceiptModal
        visible={isReceiptModalOpen}
        onClose={handleCloseReceiptModal}
        receiptData={selectedReceipt}
      />
    </>
  );
}
